import { useState } from "react";
import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  ApiProvider,
  ApprovalProvider,
  AuthProvider,
  CallbackScreen,
  LoginScreen,
  PrincipalProvider,
  makeQueryClient,
  useAuth,
  type OidcAuth,
} from "@csa/web-foundation";
import { I18nProvider, useT } from "./i18n/index";
import { AdminShell } from "./shell/admin-shell";
import { SURFACES } from "./shell/nav";
import { Dashboard } from "./surfaces/dashboard/dashboard";
import { CarrierQualityScorecard } from "./surfaces/catalog/carrier-quality-scorecard";
import { CatalogManagement } from "./surfaces/catalog/catalog-management";
import { CommissionRates } from "./surfaces/commission/commission-rates";
import { ConfigGovernance } from "./surfaces/config/config-governance";
import { Customers } from "./surfaces/customers/customers";
import { Dsar } from "./surfaces/dsar/dsar";
import { Iam } from "./surfaces/iam/iam";
import { ReportsDashboard } from "./surfaces/reports/reports-dashboard";
import { MarketingConsole } from "./surfaces/marketing/marketing-console";

// The admin surfaces with a real implementation: AL1 (carrier-quality scorecard),
// catalog management (self-service carrier + version + line-authorization),
// AL2b (commission rates), AL3 (config governance).
const SURFACE_VIEWS: Record<string, ReactNode> = {
  dashboard: <Dashboard />,
  catalog: <CarrierQualityScorecard />,
  "catalog-mgmt": <CatalogManagement />,
  customers: <Customers />,
  iam: <Iam />,
  dsar: <Dsar />,
  commission: <CommissionRates />,
  config: <ConfigGovernance />,
  reports: <ReportsDashboard />,
  marketing: <MarketingConsole />,
};

// The authenticated app: principal + shell + the routed (RBAC-gated) surfaces.
function GatedApp() {
  const { status } = useAuth();
  const { t } = useT();
  if (status === "loading") return <main className="center">{t("auth.loading")}</main>;
  if (status === "anonymous") return <LoginScreen />;
  return (
    <PrincipalProvider>
      <AdminShell surfaces={SURFACES}>
        <Routes>
          {SURFACES.map((s) => (
            <Route key={s.key} path={s.path.slice(1)} element={SURFACE_VIEWS[s.key]} />
          ))}
          <Route path="*" element={<Navigate to={SURFACES[0]!.path} replace />} />
        </Routes>
      </AdminShell>
    </PrincipalProvider>
  );
}

// Provider composition (ADR 0015 §5/§6). The OidcAuth instance is the AuthHandle the
// ApiClient consumes AND the SessionAuth the tree boots — one source of truth.
export function App({ auth }: { auth: OidcAuth }) {
  const [queryClient] = useState(makeQueryClient);
  return (
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider auth={auth}>
          <ApprovalProvider>
            <ApiProvider auth={auth}>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Routes>
                  <Route path="/auth/callback" element={<CallbackScreen auth={auth} />} />
                  <Route path="/*" element={<GatedApp />} />
                </Routes>
              </BrowserRouter>
            </ApiProvider>
          </ApprovalProvider>
        </AuthProvider>
      </QueryClientProvider>
    </I18nProvider>
  );
}
